/**
 * /system-admin/orgs — platform organization list with approve/suspend/unsuspend.
 * Search params: status (filter), q (search), page (1-based).
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { useTranslations } from "use-intl";
import { Building2 } from "lucide-react";
import { PageHeader } from "@/components/ui/page-header";
import { STANDARD_PAGE_SIZE } from "@/components/ui/pagination";
import { SystemAdminGuard } from "@/features/system-admin/components/system-admin-guard";
import { useSystemOrgs } from "@/features/system-admin/api/orgs";
import { OrgsFilters } from "@/features/system-admin/components/orgs/orgs-filters";
import { OrgsTable } from "@/features/system-admin/components/orgs/orgs-table";

const orgsSearchSchema = z.object({
  status: z.string().optional().catch(undefined),
  q: z.string().optional().catch(undefined),
  page: z.number().int().positive().optional().catch(undefined),
  pageSize: z.number().int().positive().optional().catch(undefined),
});

export const Route = createFileRoute("/system-admin/orgs/")({
  validateSearch: orgsSearchSchema.parse,
  component: SystemAdminOrgsPage,
});

function SystemAdminOrgsPage() {
  const t = useTranslations("system-admin.orgs");
  const tc = useTranslations("system-admin.common");
  const navigate = useNavigate();
  const { status = "", q = "", page = 1, pageSize = STANDARD_PAGE_SIZE } = Route.useSearch();

  const filter = {
    status: status || undefined,
    q: q || undefined,
    page,
    pageSize,
  };

  const { data, isLoading, isFetching, refetch } = useSystemOrgs(filter);

  const rows = data?.data ?? [];
  const total = data?.paging?.total ?? 0;

  function updateSearch(patch: { status?: string; q?: string; page?: number; pageSize?: number }) {
    void navigate({
      to: "/system-admin/orgs",
      search: {
        status: patch.status ?? status,
        q: patch.q ?? q,
        page: patch.page ?? 1,
        pageSize: patch.pageSize ?? pageSize,
      },
      replace: true,
    });
  }

  return (
    <SystemAdminGuard>
      <div className="space-y-6">
        <PageHeader
          icon={Building2}
          title={tc("pages.orgs.title")}
          description={tc("pages.orgs.subtitle")}
          onReload={() => refetch()}
          isReloading={isFetching}
          totalLabel={total > 0 ? t("meta.found", { count: total }) : undefined}
        >
          <OrgsFilters
            status={status}
            q={q}
            onStatusChange={(val) => updateSearch({ status: val, page: 1 })}
            onQChange={(val) => updateSearch({ q: val, page: 1 })}
          />
        </PageHeader>

        <OrgsTable
          data={rows}
          total={total}
          isLoading={isLoading}
          page={page}
          pageSize={pageSize}
          onPageChange={(p) => updateSearch({ page: p })}
          onPageSizeChange={(size) => updateSearch({ page: 1, pageSize: size })}
        />
      </div>
    </SystemAdminGuard>
  );
}
